// Typed, closed vocabulary of template merge operation kinds (mirrors C# TemplateOperationKind).
//
// The wire strings produced by toWireString are the byte-identical contract compared by OpsMatch;
// do not rename or re-case any wire value. Serialization MUST go through toWireString (an explicit
// switch), never a stringify/reflection path. The type defines the full canonical set; node's
// engines emit only the members they produce.
//
// AppView* members are produced only by the PreProcessJson AppView placeholder path, which maps a
// resolved Component* kind to its AppView* counterpart (see toAppView).

/// Single closed vocabulary of operation kinds. Each member's value equals its wire string.
export const TemplateOperationKind = {
    // Component placeholders
    ComponentReplace: 'ComponentReplace',
    ComponentSkip: 'ComponentSkip',
    ComponentMissing: 'ComponentMissing',

    // AppView placeholders (PreProcessJson maps Component* -> AppView* when a mapped AppView name resolves)
    AppViewReplace: 'AppViewReplace',
    AppViewSkip: 'AppViewSkip',
    AppViewMissing: 'AppViewMissing',

    // Slots
    SlotReplace: 'SlotReplace',
    SlotMerge: 'SlotMerge',
    SlotSkip: 'SlotSkip',
    SlotMissing: 'SlotMissing',

    // Data keys
    KeyReplace: 'KeyReplace',
    KeyMissing: 'KeyMissing',
    KeySkip: 'KeySkip',

    // Conditionals
    ConditionalBlock: 'ConditionalBlock',
    ConditionalSkip: 'ConditionalSkip',
    NegativeCondition: 'NegativeCondition',

    // Arrays
    ArrayIteration: 'ArrayIteration',
    ArraySkip: 'ArraySkip',

    // Dynamic handlers
    DynamicView: 'DynamicView',
    DynamicData: 'DynamicData',
    DynamicAction: 'DynamicAction',
} as const;

export type TemplateOperationKind = typeof TemplateOperationKind[keyof typeof TemplateOperationKind];

/// Returns the exact wire string for the kind (the OpsMatch byte contract).
export function toWireString(kind: TemplateOperationKind): string {
    switch (kind) {
        case TemplateOperationKind.ComponentReplace: return 'ComponentReplace';
        case TemplateOperationKind.ComponentSkip: return 'ComponentSkip';
        case TemplateOperationKind.ComponentMissing: return 'ComponentMissing';
        case TemplateOperationKind.AppViewReplace: return 'AppViewReplace';
        case TemplateOperationKind.AppViewSkip: return 'AppViewSkip';
        case TemplateOperationKind.AppViewMissing: return 'AppViewMissing';
        case TemplateOperationKind.SlotReplace: return 'SlotReplace';
        case TemplateOperationKind.SlotMerge: return 'SlotMerge';
        case TemplateOperationKind.SlotSkip: return 'SlotSkip';
        case TemplateOperationKind.SlotMissing: return 'SlotMissing';
        case TemplateOperationKind.KeyReplace: return 'KeyReplace';
        case TemplateOperationKind.KeyMissing: return 'KeyMissing';
        case TemplateOperationKind.KeySkip: return 'KeySkip';
        case TemplateOperationKind.ConditionalBlock: return 'ConditionalBlock';
        case TemplateOperationKind.ConditionalSkip: return 'ConditionalSkip';
        case TemplateOperationKind.NegativeCondition: return 'NegativeCondition';
        case TemplateOperationKind.ArrayIteration: return 'ArrayIteration';
        case TemplateOperationKind.ArraySkip: return 'ArraySkip';
        case TemplateOperationKind.DynamicView: return 'DynamicView';
        case TemplateOperationKind.DynamicData: return 'DynamicData';
        case TemplateOperationKind.DynamicAction: return 'DynamicAction';
    }
}

/// Maps a Component* kind to its AppView* counterpart (PreProcessJson AppView placeholder path).
export function toAppView(kind: TemplateOperationKind): TemplateOperationKind {
    switch (kind) {
        case TemplateOperationKind.ComponentReplace: return TemplateOperationKind.AppViewReplace;
        case TemplateOperationKind.ComponentSkip: return TemplateOperationKind.AppViewSkip;
        case TemplateOperationKind.ComponentMissing: return TemplateOperationKind.AppViewMissing;
        default: return kind;
    }
}

/// Parses a wire string back to its kind, or null if unrecognized (read-back/validation path).
export function tryParseWire(wire: string): TemplateOperationKind | null {
    switch (wire) {
        case 'ComponentReplace': return TemplateOperationKind.ComponentReplace;
        case 'ComponentSkip': return TemplateOperationKind.ComponentSkip;
        case 'ComponentMissing': return TemplateOperationKind.ComponentMissing;
        case 'AppViewReplace': return TemplateOperationKind.AppViewReplace;
        case 'AppViewSkip': return TemplateOperationKind.AppViewSkip;
        case 'AppViewMissing': return TemplateOperationKind.AppViewMissing;
        case 'SlotReplace': return TemplateOperationKind.SlotReplace;
        case 'SlotMerge': return TemplateOperationKind.SlotMerge;
        case 'SlotSkip': return TemplateOperationKind.SlotSkip;
        case 'SlotMissing': return TemplateOperationKind.SlotMissing;
        case 'KeyReplace': return TemplateOperationKind.KeyReplace;
        case 'KeyMissing': return TemplateOperationKind.KeyMissing;
        case 'KeySkip': return TemplateOperationKind.KeySkip;
        case 'ConditionalBlock': return TemplateOperationKind.ConditionalBlock;
        case 'ConditionalSkip': return TemplateOperationKind.ConditionalSkip;
        case 'NegativeCondition': return TemplateOperationKind.NegativeCondition;
        case 'ArrayIteration': return TemplateOperationKind.ArrayIteration;
        case 'ArraySkip': return TemplateOperationKind.ArraySkip;
        case 'DynamicView': return TemplateOperationKind.DynamicView;
        case 'DynamicData': return TemplateOperationKind.DynamicData;
        case 'DynamicAction': return TemplateOperationKind.DynamicAction;
        default: return null;
    }
}
